import { useState } from "react";
import { toast } from "sonner";
import { Search, ArrowRight, Loader2, FileDown, Mail, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const INPUT = "w-full bg-navy border border-white/15 text-white px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors";

export function SkillsTranslator() {
  const [sf, setSf] = useState({ branch: "", mos: "", years: "", interests: "" });
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const [res, setRes] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const setS = (k, v) => setSf((f) => ({ ...f, [k]: v }));

  const sendEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Please enter a valid email address."); return; }
    setEmailBusy(true);
    try {
      await api.post("/skills-translator/email", { email: email.trim(), branch: sf.branch, mos: sf.mos, years: sf.years, skills: res.skills || [], ideas: res.ideas || [] });
      toast.success("Sent! Check your inbox for your ideas.");
      setEmailOpen(false); setEmail("");
    } catch (e) { toast.error("Could not send — please try again."); } finally { setEmailBusy(false); }
  };

  const download = async () => {
    setDl(true);
    try {
      const r = await api.post("/skills-translator/pdf",
        { branch: sf.branch, mos: sf.mos, years: sf.years, skills: res.skills || [], ideas: res.ideas || [] },
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "my-business-ideas.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not create PDF — please try again."); } finally { setDl(false); }
  };

  const translate = async (e) => {
    e.preventDefault();
    if (!sf.mos.trim() && !sf.branch.trim() && !sf.interests.trim()) { toast.error("Add your branch, MOS or interests first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/skills-translator", sf);
      setRes(data);
      if (!data.ok || !(data.ideas || []).length) toast.error("Couldn't generate ideas right now — please try again in a moment.");
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <section id="skills" className="border-y border-white/10 bg-navy-800/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3"><Search size={15} /> Military Skills Translator</div>
        <h2 className="font-heading text-3xl text-white mb-3">Turn your MOS into business ideas</h2>
        <p className="text-gray-400 mb-8 max-w-2xl">Tell us about your service and Top Foster will instantly translate your military experience into business ideas that fit your skills.</p>
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <form onSubmit={translate} className="border border-white/10 bg-navy p-6 space-y-4" data-testid="skills-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input className={INPUT} placeholder="Branch (e.g. Army)" value={sf.branch} onChange={(e) => setS("branch", e.target.value)} data-testid="skills-branch" />
              <input className={INPUT} placeholder="Years of service (e.g. 12)" value={sf.years} onChange={(e) => setS("years", e.target.value)} data-testid="skills-years" />
            </div>
            <input className={INPUT} placeholder="MOS / AFSC / Rating (e.g. 92A Logistics)" value={sf.mos} onChange={(e) => setS("mos", e.target.value)} data-testid="skills-mos" />
            <textarea className={`${INPUT} resize-none`} rows={3} placeholder="Interests or what you'd love to do (optional)" value={sf.interests} onChange={(e) => setS("interests", e.target.value)} data-testid="skills-interests" />
            <button type="submit" disabled={busy} data-testid="skills-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <>Translate my experience <ArrowRight size={16} /></>}
            </button>
          </form>
          <div data-testid="skills-results">
            {!res ? (
              <div className="border border-dashed border-white/15 p-8 text-center text-gray-500 text-sm h-full flex items-center justify-center min-h-[220px]">Your matched business ideas will appear here.</div>
            ) : (
              <div className="space-y-4">
                {(res.skills || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">{res.skills.map((s) => <span key={s} className="bg-gold/15 text-gold border border-gold/30 px-3 py-1 text-xs">{s}</span>)}</div>
                )}
                {(res.ideas || []).map((it, i) => (
                  <div key={i} className="border border-white/10 bg-navy p-5" data-testid={`skills-idea-${i}`}>
                    <h3 className="font-heading text-lg text-white mb-1.5">{it.title}</h3>
                    <p className="text-gray-400 text-sm mb-2">{it.why_fit}</p>
                    {it.first_step && <p className="text-sm text-gray-300"><span className="text-gold font-semibold">First step:</span> {it.first_step}</p>}
                  </div>
                ))}
                {(res.ideas || []).length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-4">
                      <a href="/start" className="inline-flex items-center gap-2 text-gold text-sm font-semibold hover:gap-3 transition-all" data-testid="skills-start-cta">Start building one in the assessment <ArrowRight size={15} /></a>
                      <button onClick={download} disabled={dl} data-testid="skills-download-pdf"
                        className="inline-flex items-center gap-1.5 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors disabled:opacity-60">
                        {dl ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />} Download my ideas (PDF)
                      </button>
                      <button onClick={() => setEmailOpen((v) => !v)} data-testid="skills-email-toggle"
                        className="inline-flex items-center gap-1.5 text-sm border border-white/15 text-white px-4 py-2 hover:border-gold/50 transition-colors">
                        <Mail size={14} /> Email it to me
                      </button>
                    </div>
                    {emailOpen && (
                      <div className="flex items-center gap-2" data-testid="skills-email-form">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                          data-testid="skills-email-input" onKeyDown={(e) => e.key === "Enter" && sendEmail()}
                          className="flex-1 bg-navy border border-white/20 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                        <button onClick={sendEmail} disabled={emailBusy} data-testid="skills-email-send"
                          className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
                          {emailBusy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
